import React from 'react';

import { api } from '../../../../../core/api';
import { BenchmarkView } from '../../../../../generated/model/benchmarkView';
import { FinancialDetailResult } from '../../../../../generated/model/financialDetailResult';
import CompanyTabContent from '../shared/CompanyTabContent';
import { consolidationToggleState } from '../shared/companyTabs.utils';
import ConsolidationToggle from '../shared/ConsolidationToggle/ConsolidationToggle';
import { useConsolidationToggleStorage } from '../shared/ConsolidationToggle/useConsolidationToggleStorage';
import { useCompanyFinTabLoader } from '../shared/useCompanyTabLoader';
import FinancialsBalanceSheet from './FinancialsBalanceSheet';
import FinancialsPL from './FinancialsPL';
import FinancialStatments from './FinancialStatements';
import OtherFinancialInfo from './OtherFinancialInfo';
import TpAdjusterPLI from './TpAdjustedPLI';

interface Props {
  consolidationPreference?: BenchmarkView.ConsolidationPreferenceEnum;
}

const CompanyFinancials: React.FC<Props> = ({ consolidationPreference }) => {
  const { data, isLoading } = useCompanyFinTabLoader(api.company.getFinancialsData);
  const { toggleValue, onToggleChange } = useConsolidationToggleStorage();

  const hasStatementDetails =
    data?.financialStatementDetails &&
    Array.isArray(data?.financialStatementDetails) &&
    data?.financialStatementDetails?.length;
  const hasAdjustedPliDetails = adjustedPliHasDetails(data);
  const hasOtherFinancialInfoDetails = otherFinancialInfoHasDetails(data);

  function adjustedPliHasDetails(data: undefined | FinancialDetailResult) {
    if (
      data?.tpAdjustedDetails &&
      Array.isArray(data?.tpAdjustedDetails) &&
      data?.tpAdjustedDetails?.length &&
      data.tpAdjustedDetails.some(
        (item) =>
          item?.tpAdjustedTurnover ||
          item?.tpAdjustedCostOfGoodsSold ||
          item?.tpAdjustedGrossProfitLoss ||
          item?.tpAdjustedOperatingProfitLossEbit ||
          item?.tpAdjustedOperatingExpenses,
      )
    ) {
      return true;
    }
    return false;
  }

  function otherFinancialInfoHasDetails(data: undefined | FinancialDetailResult) {
    if (
      data?.otherFinancialDetails &&
      Array.isArray(data?.otherFinancialDetails) &&
      data?.otherFinancialDetails?.length &&
      data.otherFinancialDetails.some((item) => item?.employeeAverage || item?.accountingStandard)
    ) {
      return true;
    }
    return false;
  }

  function hasOfConsolidationType(consolidation: boolean) {
    return (
      (data?.profitLossDetails?.some((d) => d.consolidation === consolidation) ?? false) ||
      (data?.tpAdjustedDetails?.some((d) => d.consolidation === consolidation) ?? false) ||
      (data?.balanceDetails?.some((d) => d.consolidation === consolidation) ?? false) ||
      (data?.financialStatementDetails?.some((d) => d.consolidation === consolidation) ?? false) ||
      (data?.otherFinancialDetails?.some((d) => d.consolidation === consolidation) ?? false)
    );
  }
  const hasConsolidated = hasOfConsolidationType(true);
  const hasUnconsolidated = hasOfConsolidationType(false);
  const { showConsolidationToggle, consolidation } = consolidationToggleState(
    hasConsolidated,
    hasUnconsolidated,
    consolidationPreference,
    toggleValue,
  );
  const showConversionRates = data?.currency !== 'ACTUAL';

  return (
    <CompanyTabContent isLoading={isLoading}>
      {showConsolidationToggle && (
        <ConsolidationToggle toggleValue={toggleValue} onToggleChange={onToggleChange} />
      )}
      <FinancialsPL
        data={data?.profitLossDetails?.filter((d) => d.consolidation === consolidation)}
        ccy={data?.currency}
        consolidation={consolidation}
        showConversionRate={showConversionRates}
      />
      {Boolean(hasStatementDetails) && (
        <FinancialStatments
          data={data?.financialStatementDetails?.filter((d) => d.consolidation === consolidation)}
          ccy={data?.currency}
        />
      )}
      {Boolean(hasAdjustedPliDetails) && (
        <TpAdjusterPLI
          data={data?.tpAdjustedDetails?.filter((d) => d.consolidation === consolidation)}
          ccy={data?.currency}
        />
      )}
      <FinancialsBalanceSheet
        data={data?.balanceDetails?.filter((d) => d.consolidation === consolidation)}
        ccy={data?.currency}
        showConversionRate={showConversionRates}
      />
      {Boolean(hasOtherFinancialInfoDetails) && (
        <OtherFinancialInfo
          data={data?.otherFinancialDetails?.filter((d) => d.consolidation === consolidation)}
          ccy={data?.currency}
          consolidation={consolidation}
        />
      )}
    </CompanyTabContent>
  );
};

export default CompanyFinancials;
